import { useParams } from "react-router"
import { Box, Divider, Link, List, Stack, Typography } from "@mui/material"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { TreeToolbar } from "slot-definitions"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { NoResults } from "lib/@components/no-results/no-results"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useReferenceState } from "lib/@hooks/use-reference"
import { navigateSchedulePreview } from "authoring/routes/project/utilities/project-navigation"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { AmendsViewAllToggle } from "authoring/routes/project/amends/components/amends-view-all-toggle"
import { getScheduleCommentaryAuthorNotes } from "./controller/get-schedule-commentary-author-notes"
import { ScheduleCommentaryHideUnchangedToggle } from "./components/schedule-commentary-hide-unchanged-toggle"
import { ScheduleCommentaryAuthorNoteSection } from "./components/schedule-commentary-author-note-section"
import { ScheduleCommentaryLegislationItem } from "./components/schedule-commentary-legislation-item"

// Read-only - built live from the draft's author notes on every load, nothing persisted here.
// The old edit/review/approve workflow that used to live on this page has moved to the
// publish-commentary page, working from a snapshot taken when the schedule is sent to legacy.
export function ScheduleCommentary() {
    const { projectId, scheduleId } = useParams()
    const notes = getScheduleCommentaryAuthorNotes.useResults({ projectId, scheduleId }, withInitialValue(IS_LOADING))
    const draftId = createDraftId(projectId, scheduleId)
    const [hideUnchanged] = useReferenceState(`scheduleCommentary-hideUnchanged-${draftId}`, false)

    const parentCrumbs = [
        <Link
            key="preview"
            sx={{ cursor: "pointer" }}
            onClick={() => navigateSchedulePreview({ projectId, scheduleId })}
        >
            Preview
        </Link>,
    ]

    TreeToolbar.usePlug(
        <If value={Boolean(notes)}>
            <Stack direction="row" spacing={2} alignItems="center">
                <AmendsViewAllToggle projectId={projectId} scheduleId={scheduleId} keyLabel="scheduleCommentary" />
                <ScheduleCommentaryHideUnchangedToggle storageKey={`scheduleCommentary-hideUnchanged-${draftId}`} />
            </Stack>
        </If>,
        [Boolean(notes)]
    )

    if (notes === IS_LOADING) return <LoadingScreen />

    return (
        <PageBreadcrumbContainer title="View Commentary" parentCrumbs={parentCrumbs}>
            <If value={Boolean(notes)}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Repeat
                        list={getVisibleSections()}
                        item={<ScheduleCommentaryAuthorNoteSection projectId={projectId} scheduleId={scheduleId} />}
                        pass="note"
                        keyFn={(note) => note.amendsKey}
                    />
                    <If value={notes?.legislation?.length > 0}>
                        <Box>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Hyperlinks
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Legislation, Regulations and Guidance
                            </Typography>
                            <List sx={{ listStyleType: "disc", pl: 3 }}>
                                <Repeat list={notes?.legislation} item={<ScheduleCommentaryLegislationItem />} />
                            </List>
                        </Box>
                    </If>
                </Stack>
                <Else>
                    <NoResults>Commentary isn't available for this schedule.</NoResults>
                </Else>
            </If>
        </PageBreadcrumbContainer>
    )

    function getVisibleSections() {
        if (!notes) return []
        const sections = [notes.leadingSummary, notes.introContent, notes.introNote, ...(notes.tasks ?? [])].filter(
            Boolean
        )
        return hideUnchanged ? sections.filter((note) => note.hasAmends) : sections
    }
}
